import { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { SttForm } from '../../../sporttia/SttForm';
import { AppContext } from '../../../sporttia/AppContext';
import { subsetObject } from '../../../lib/utils';
import {
    lockFormElements,
    useCanModifyConfiguration,
} from './useCanModifyConfiguration';

type Web = {
    id?: number;
    name?: string;
    description?: string;
    slogan?: string;
    address?: string;
    city?: string;
    email?: string;
    phone?: string;
    facebook?: string;
    twitter?: string;
    youtube?: string;
    rates?: string;
    availability?: string;
};

type WebResponse = {
    count: number;
    rows: Web[];
};

export default function ConfigurationWeb(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const canModify = useCanModifyConfiguration();
    const [web, setWeb] = useState<Web>({});

    const scId = cxt.sc?.id;

    /**
     * Save sport center website.
     */
    const saveSportCenterWebSite = (): void => {
        const params = subsetObject(web, [
            'name',
            'description',
            'slogan',
            'address',
            'city',
            'email',
            'phone',
            'facebook',
            'twitter',
            'youtube',
            'rates',
            'availability',
        ]);

        if (web && web.id) {
            cxt?.api('PUT', `/webs/${web.id}`, {
                params,
                success: () => {
                    cxt.showMessage('S', t('Saved'));
                },
            });
        }
    };

    /**
     * Handle form.
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (!name) return;
        setWeb({ ...web, [name]: value });
    };

    /**
     * When we load "web" tab for first time we will get sport center website data from database.
     */
    useEffect(() => {
        if (!scId) return;

        cxt?.api('GET', `/scs/${scId}/webs`, {
            version: 'v7',
            success: (r: WebResponse) => {
                if (r.count > 0 && r.rows[0]) {
                    setWeb(r.rows[0]);
                }
            },
        });
    }, [scId]);

    // -----| Render |-----
    return (
        <Box>
            <SttForm
                onChangeForm={handleForm}
                elements={lockFormElements(
                    [
                        {
                            md: 12,
                            sm: 12,
                            xs: 12,
                            caption: t('Name'),
                            name: 'name',
                            value: web.name,
                        },
                        {
                            md: 12,
                            sm: 12,
                            xs: 12,
                            caption: t('Slogan'),
                            name: 'slogan',
                            value: web.slogan,
                        },
                        {
                            md: 12,
                            sm: 12,
                            xs: 12,
                            type: 'textarea',
                            caption: t('Description'),
                            name: 'description',
                            value: web.description,
                        },
                        {
                            md: 6,
                            sm: 6,
                            xs: 12,
                            caption: t('Address'),
                            name: 'address',
                            value: web.address,
                        },
                        {
                            md: 6,
                            sm: 6,
                            xs: 12,
                            caption: t('City'),
                            name: 'city',
                            value: web.city,
                        },
                        {
                            md: 6,
                            sm: 6,
                            xs: 12,
                            caption: t('Email'),
                            name: 'email',
                            value: web.email,
                        },
                        {
                            md: 6,
                            sm: 6,
                            xs: 12,
                            caption: t('Phone'),
                            name: 'phone',
                            value: web.phone,
                        },
                        {
                            md: 6,
                            sm: 6,
                            xs: 12,
                            caption: 'Facebook',
                            name: 'facebook',
                            value: web.facebook,
                        },
                        {
                            md: 6,
                            sm: 6,
                            xs: 12,
                            caption: 'Twitter',
                            name: 'twitter',
                            value: web.twitter,
                        },
                        {
                            md: 12,
                            sm: 12,
                            xs: 12,
                            caption: 'Youtube',
                            name: 'youtube',
                            value: web.youtube,
                        },
                        {
                            md: 12,
                            sm: 12,
                            xs: 12,
                            type: 'textarea',
                            caption: t('Rates'),
                            name: 'rates',
                            value: web.rates,
                        },
                        {
                            md: 12,
                            sm: 12,
                            xs: 12,
                            type: 'textarea',
                            caption: t('Availability'),
                            name: 'availability',
                            value: web.availability,
                        },
                    ],
                    !canModify,
                )}
                buttons={
                    canModify
                        ? [
                              {
                                  type: 'save',
                                  onClick: () => saveSportCenterWebSite(),
                              },
                          ]
                        : []
                }
            />
        </Box>
    );
}
